import ScrollToTopButton from "components/ScrollToTopButton";
import { AddCartDetailsShimmer } from "components/shimmer/addCartDetailsShimmer";
import { useEffect, useState } from "react";
import { BsCartXFill, BsTruck } from "react-icons/bs";
import { FaMapPin } from "react-icons/fa";
import { HiOutlineTrash, HiOutlineX } from "react-icons/hi";
import { TbCurrencyTaka } from "react-icons/tb";
import { Link, useNavigate } from "react-router-dom";
import ScreenScrollToTop from "../components/ScreenScrollToTop";
import { useSettings } from "context/SettingsContext";
import { t } from "context/translations";
import { getCartItems, setCartItems } from "utils/cartStorage";
import { assetUrl } from "utils/api";

const AddCartDetails = () => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState([]);
  const { theme, language } = useSettings();
  const isDark = theme === "dark";

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      const storedItems = getCartItems();
      setProducts(storedItems);
      setLoading(false);
    }, 500);
  }, []);

  const updateLocalStorage = (items) => {
    setCartItems(items);
  };

  const selectedProducts = selectedItems.map((index) => products[index]).filter(Boolean);
  const selectedCount = selectedProducts.length;

  const increaseQuantity = (index) => {
    const updated = [...products];
    updated[index].quantity = (updated[index].quantity || 1) + 1;
    setProducts(updated);
    updateLocalStorage(updated);
  };

  const decreaseQuantity = (index) => {
    const updated = [...products];
    if ((updated[index].quantity || 1) > 1) {
      updated[index].quantity -= 1;
      setProducts(updated);
      updateLocalStorage(updated);
    }
  };

  const removeItem = (index) => {
    const updated = [...products];
    updated.splice(index, 1); // remove the item
    setProducts(updated);
    updateLocalStorage(updated);
  };

  const subtotal = selectedProducts.reduce(
    (total, item) =>
      total + (item.selectedVariant?.flashPrice || 0) * (item.quantity || 1),
    0,
  );

  const handleProceedToCheckout = () => {
    if (selectedProducts.length === 0) return;

    localStorage.setItem("checkoutItems", JSON.stringify(selectedProducts));
    navigate("/orderDetails");
  };

  return (
    <div className="pb-24 md:pb-0">
      <div className="flex-1 mb-3 relative md:max-w-8xl sm:px-4 2xl:px-0 lg:mx-8 md:pb-4 pb-0">
        {loading ? (
          <AddCartDetailsShimmer />
        ) : products.length === 0 ? (
            <div className={`flex flex-1 flex-col justify-center items-center text-center ${isDark?"text-white":"text-gray-800"} py-10`}>
            <BsCartXFill className="mx-auto w-24 h-24 sm:w-40 sm:h-40 mb-5 opacity-60" />
            <h3 className="text-base sm:text-lg font-semibold mb-2 mt-5">
              {t("emptyCart", language)}
            </h3>
          </div>
        ) : (
          <>
            <div className="flex flex-col lg:flex-row gap-6 mt-2">
              <div
                className={`py-2 px-4 w-full  ${isDark ? "bg-gray-100 border-gray-600 text-white " : "border-gray-300"}`}
              >
                <div>
                  <div className="flex justify-between items-center rounded  ">
                    <div className="flex justify-center items-center">
                      <input
                        type="checkbox"
                        className={`mr-2 w-4 h-4 rounded border border-blue-300 appearance-none cursor-pointer
                          ${isDark ? "bg-gray-800 checked:bg-blue-600" : "bg-white checked:bg-blue-600"}
                        `}
                        checked={
                          products.length > 0 &&
                          selectedItems.length === products.length
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedItems(products.map((_, idx) => idx));
                          } else {
                            setSelectedItems([]);
                          }
                        }}
                      />
                      <div className="font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent sm:text-lg">
                        {t("myCart", language)} ({products.length})
                      </div>
                    </div>
                    <div className="flex items-center sm:gap-5 gap-2">
                      <div>
                        <button
                          className={`py-1 rounded  ${
                            selectedItems.length > 0
                              ? "  text-red-600 cursor-pointer"
                              : "  text-gray-400 cursor-not-allowed"
                          }`}
                          onClick={() => {
                            const updated = products.filter(
                              (_, idx) => !selectedItems.includes(idx),
                            );
                            setProducts(updated);
                            updateLocalStorage(updated);
                            setSelectedItems([]); // clear selection
                          }}
                          disabled={selectedItems.length === 0} // disable if no items selected
                        >
                          <HiOutlineTrash className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>

                  {products.map((item, index) => (
                    <div
                      key={index}
                      className="flex flex-col md:grid md:grid-cols-6 gap-0 md:gap-4 items-center mt-2 md:py-4 py-1 border-b"
                    >
                      {/* Product Info */}
                      <div className="flex gap-1 items-center col-span-3 w-full">
                        <input
                          type="checkbox"
                          className="w-4 h-4 text-blue-600 border-blue-400 rounded mr-2"
                          checked={selectedItems.includes(index)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedItems([...selectedItems, index]);
                            } else {
                              setSelectedItems(
                                selectedItems.filter((i) => i !== index),
                              );
                            }
                          }}
                        />

                        <img
                          src={assetUrl(item.selectedVariant.images[0].thumb)}
                          alt={item.selectedVariant?.title}
                          loading="lazy"
                          decoding="async"
                          className="w-16 h-16 sm:w-20 sm:h-20 object-top object-cover rounded"
                        />
                        <div className="flex-1 min-w-0 px-1">
                          <div className="flex justify-between">
                            <div className="min-w-0">
                              <Link
                                to={`/details/${item.catalogue}/${item.item_id}`}
                                className="text-sm hidden md:flex truncate"
                              >
                                {item.selectedVariant?.title}
                              </Link>
                              <Link
                                to={`/details/${item.catalogue}/${item.item_id}`}
                              >
                                <p
                                  className={`text-xs sm:text-sm ${isDark ? "text-white" : "text-gray-900"}  mt-1 md:hidden truncate font-bold`}
                                >
                                  {item.selectedVariant?.title}
                                </p>
                              </Link>
                            </div>
                            <div className="shrink-0 md:hidden">
                              <button
                                className="text-red-600 text-xs sm:text-sm"
                                onClick={() => removeItem(index)}
                              >
                                <HiOutlineX className="w-4 h-4 mr-1" />
                              </button>
                            </div>
                          </div>
                          <div className="text-xs text-gray-600">
                            <h1> {item.catalogue_title}</h1>
                          </div>
                          <div className="flex justify-between md:hidden">
                            <div
                              className={`text-xs flex font-semibold items-center ${isDark ? "text-white" : "text-backgroundColor"}`}
                            >
                              {(
                                (item.selectedVariant?.flashPrice || 0) *
                                (item.quantity || 1)
                              ).toFixed(0)}
                              <TbCurrencyTaka />
                            </div>
                            <div className="flex items-center">
                              <button
                                className=" w-6 h-6 rounded disabled:opacity-50 bg-gray-200"
                                onClick={() => decreaseQuantity(index)}
                                disabled={(item.quantity || 1) <= 1}
                              >
                                -
                              </button>
                              <span className="w-6 text-center text-xs">
                                {item.quantity || 1}
                              </span>
                              <button
                                className={`border-2 border-darkBgColor w-6 h-6 flex items-center justify-center ${isDark ? "text-white" : "text-backgroundColor"}  rounded`}
                                onClick={() => increaseQuantity(index)}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Price - Desktop */}
                      <div className="hidden md:flex justify-center items-center text-backgroundColor">
                        <span className="text-sm">
                          {t("TK", language)} {item.selectedVariant?.flashPrice}
                        </span>
                      </div>

                      {/* Quantity Controls - Desktop */}
                      <div className="hidden md:flex items-center justify-center space-x-2">
                        <button
                          className="border-2 border-darkBgColor w-8 h-8 flex items-center justify-center text-red-600 rounded disabled:opacity-50"
                          onClick={() => decreaseQuantity(index)}
                          disabled={(item.quantity || 1) <= 1}
                        >
                          -
                        </button>
                        <span className="w-8 text-center">
                          {item.quantity || 1}
                        </span>
                        <button
                          className={`border-2 border-darkBgColor w-8 h-8 flex items-center justify-center ${isDark ? "text-white" : "text-backgroundColor"}  rounded`}
                          onClick={() => increaseQuantity(index)}
                        >
                          +
                        </button>
                      </div>

                      {/* Mobile View - Price and Quantity */}
                      <div className="md:hidden w-full mt-2">
                        <div className="flex justify-between items-center">
                          <div></div>
                        </div>
                      </div>

                      {/* Total - Desktop */}
                      <div className="flex items-center justify-center">
                        {/* <div className="hidden md:flex justify-end font-medium items-center text-backgroundColor">
                            {(
                              (item.selectedVariant?.flashPrice || 0) *
                              (item.quantity || 1)
                            ).toFixed(2)}
                            <TbCurrencyTaka />
                          </div> */}
                        <button
                          className="text-red-600 text-xs sm:text-sm mt-1 hidden md:flex"
                          onClick={() => removeItem(index)}
                        >
                          <HiOutlineTrash className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                  {/* </> */}
                  {/* )} */}
                </div>
              </div>
              <div className="w-full lg:w-1/3 mb-5 ">
                <div className="sticky top-24 lg:border-2 lg:rounded-lg">
                  <div className="px-4 py-3 md:py-5 rounded hidden sm:block">
                    {" "}
                    <h2 className="lg:text-xl md:text-lg text-base font-bold  mb-3 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
                      {t("deliveryCharge", language)}
                    </h2>
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <div className="p-2 rounded-full bg-green-100 text-green-600">
                          <FaMapPin size={20} />
                        </div>
                        <div className="w-full">
                          <div className="flex justify-between">
                            <p
                              className={`text-sm ${isDark ? "text-white" : "text-gray-800"} `}
                            >
                              {t("insideDhaka", language)}
                            </p>
                            <p
                              className={`text-sm ${isDark ? "text-white" : "text-gray-800"} `}
                            >
                              80 {t("TK", language)}
                            </p>
                          </div>
                          <p
                            className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {t("DeliveryOneDay", language)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start gap-4">
                        <div className="p-2 rounded-full bg-blue-100 text-blue-600">
                          <BsTruck size={20} />
                        </div>
                        <div className="w-full">
                          <div className="flex justify-between">
                            <p
                              className={`text-sm ${isDark ? "text-white" : "text-gray-800"} `}
                            >
                              {t("outsideDhaka", language)}
                            </p>
                            <p
                              className={`text-sm ${isDark ? "text-white" : "text-gray-800"} `}
                            >
                              150 {t("TK", language)}
                            </p>
                          </div>
                          <p
                            className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {t("DeliveryManyDay", language)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`mt-4 px-4 py-2 ${isDark ? "bg-gray-100" : "bg-white"}  md:py-5 rounded hidden sm:block`}
                  >
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 font-semibold">
                          {t("subTotal", language)} ({selectedCount} items)
                        </span>
                        <span className="text-black font-semibold">
                          {subtotal} {t("TK", language)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500 font-semibold">
                          {t("deliveryFee", language)}
                        </span>
                        <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent">
                          {t("toBeAdded", language)}
                        </span>
                      </div>
                      <div className="font-semibold border-dashed border-t border-gray-500 pt-1 flex justify-between text-md">
                        <span className="text-gray-500 font-semibold">
                          {t("totalCost", language)}
                        </span>
                        <span className="text-backgroundColor">
                          {subtotal} {t("TK", language)}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={handleProceedToCheckout}
                      disabled={selectedCount === 0}
                      className="w-full bg-darkBgColor text-white md:py-2 py-2 rounded-full mt-4 transition-colors lg:text-lg text-sm  font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("nextStep", language)}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {products.length > 0 && (
          <div
            className={`fixed bottom-14 left-0 w-full z-50 ${isDark ? "bg-gray-100" : "bg-white"} border-t border-slate-200 px-5 py-2 sm:hidden`}
          >
            <div className="flex justify-between text-sm ">
              <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 bg-clip-text text-transparent font-semibold">
                {t("subTotal", language)} ({selectedCount} items)
              </span>
              <span className="text-black font-semibold">
                {subtotal} {t("TK", language)}
              </span>
            </div>
            <button
              onClick={handleProceedToCheckout}
              disabled={selectedCount === 0}
              className="w-full bg-darkBgColor text-white md:py-2 py-2 rounded-full mt-2 transition-colors lg:text-lg text-sm  font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("nextStep", language)}
            </button>
          </div>
        )}
      </div>
      <ScrollToTopButton />
    </div>
  );
};

export default AddCartDetails;
